import { useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";

const accentMap: Record<string, string> = {
  cyan: "text-cyan-400",
  orange: "text-orange-400",
  emerald: "text-emerald-400",
  violet: "text-violet-400",
};
const borderMap: Record<string, string> = {
  cyan: "border-cyan-500/30 hover:border-cyan-400/70",
  orange: "border-orange-500/30 hover:border-orange-400/70",
  emerald: "border-emerald-500/30 hover:border-emerald-400/70",
  violet: "border-violet-500/30 hover:border-violet-400/70",
};
const glowMap: Record<string, string> = {
  cyan: "from-cyan-500",
  orange: "from-orange-500",
  emerald: "from-emerald-500",
  violet: "from-violet-500",
};
const tagBgMap: Record<string, string> = {
  cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  orange: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
};
const previewBgMap: Record<string, string> = {
  cyan: "from-[#071a20] via-[#0a1e28] to-[#0d1428]",
  orange: "from-[#1a0f07] via-[#20120a] to-[#0d1428]",
  emerald: "from-[#071a0f] via-[#0a1e12] to-[#0d1428]",
  violet: "from-[#0f0720] via-[#130a28] to-[#0d1428]",
};
const specLabelMap: Record<string, string> = {
  cyan: "text-cyan-400/70",
  orange: "text-orange-400/70",
  emerald: "text-emerald-400/70",
  violet: "text-violet-400/70",
};
const dividerMap: Record<string, string> = {
  cyan: "border-cyan-500/20",
  orange: "border-orange-500/20",
  emerald: "border-emerald-500/20",
  violet: "border-violet-500/20",
};
const btnMap: Record<string, string> = {
  cyan: "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/15",
  orange: "border-orange-500/50 text-orange-400 hover:bg-orange-500/15",
  emerald: "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/15",
  violet: "border-violet-500/50 text-violet-400 hover:bg-violet-500/15",
};

const categoryIcons: Record<string, string> = {
  "aero-engine": "ri-fire-line",
  "uav-propulsion": "ri-flight-takeoff-line",
  "precision-mfg": "ri-settings-3-line",
  "tech-services": "ri-flask-line",
};

function highlight(text: string, keyword: string): ReactElement {
  if (!keyword.trim()) return <>{text}</>;
  const kw = keyword.trim();
  const regex = new RegExp(`(${kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-400/80 text-[#070c1a] rounded px-0.5 not-italic font-semibold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

interface Spec {
  label: string;
  value: string;
}
interface LocaleData {
  title: string;
  subtitle: string;
  tags: string[];
  specs: Spec[];
  desc: string;
  features: string[];
}
interface Product {
  id: string;
  category: string;
  accent: string;
  images: string[];
  zh: LocaleData;
  en: LocaleData;
}
interface ProductCardProps {
  product: Product;
  lang: "zh" | "en";
  searchKeyword: string;
  categoryLabel: string;
}

export default function ProductCard({ product: p, lang, searchKeyword, categoryLabel }: ProductCardProps) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const accent = p.accent;
  const t = lang === "zh" ? p.zh : p.en;
  const detailTarget = p.category === "tech-services" ? "/news" : `/products/${p.id}`;

  return (
    <div
      onClick={() => navigate(detailTarget)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`group relative rounded-2xl border bg-[#0d1428] overflow-hidden transition-all duration-300 cursor-pointer ${borderMap[accent]} ${hovered ? "scale-[1.02]" : ""}`}
    >
      {/* Image */}
      <div className="relative w-full h-52 overflow-hidden flex-shrink-0">
        <img
          src={p.images[0]}
          alt={t.title}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${hovered ? "scale-110" : "scale-100"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1428] via-[#0d1428]/20 to-transparent" />
        {p.images.length > 1 && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-sm text-gray-300 text-xs">
            <i className="ri-image-2-line text-xs" />
            {p.images.length}
          </div>
        )}
        <div className={`absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${tagBgMap[accent]}`}>
          <i className={`${categoryIcons[p.category]} mr-1`} />
          {categoryLabel}
        </div>
      </div>

      {/* Normal content */}
      <div className="p-5">
        <p className="text-gray-500 text-xs mb-1">{highlight(t.subtitle, searchKeyword)}</p>
        <h3 className={`text-base font-bold mb-2.5 ${accentMap[accent]}`}>{highlight(t.title, searchKeyword)}</h3>
        <p className="text-gray-400 text-xs leading-relaxed mb-4 line-clamp-3">{highlight(t.desc, searchKeyword)}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {t.tags.map((tag, j) => (
            <span key={j} className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-gray-400 border border-white/10 whitespace-nowrap">
              {highlight(tag, searchKeyword)}
            </span>
          ))}
        </div>

        <div className={`flex items-center gap-1.5 text-xs font-semibold transition-all duration-300 ${accentMap[accent]} ${hovered ? "gap-2.5" : ""}`}>
          <span>{lang === "zh" ? "查看详情" : "View Details"}</span>
          <i className="ri-arrow-right-line" />
        </div>
      </div>

      {/* Hover Quick Preview Panel */}
      <div
        className={`absolute inset-0 rounded-2xl bg-gradient-to-b ${previewBgMap[accent]} flex flex-col transition-all duration-350 ${hovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        style={{ backdropFilter: "blur(2px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Preview header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <p className="text-gray-500 text-xs mb-0.5">{t.subtitle}</p>
            <h3 className={`text-sm font-bold leading-tight ${accentMap[accent]}`}>{t.title}</h3>
          </div>
          <div className={`w-8 h-8 flex items-center justify-center rounded-full border ${tagBgMap[accent]}`}>
            <i className={`${categoryIcons[p.category]} text-sm`} />
          </div>
        </div>

        {/* Divider */}
        <div className={`mx-5 border-t ${dividerMap[accent]}`} />

        {/* Specs grid */}
        <div className="px-5 py-3 flex-1">
          <p className={`text-xs font-semibold mb-2.5 uppercase tracking-wider ${specLabelMap[accent]}`}>
            {lang === "zh" ? "核心技术参数" : "Key Specifications"}
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {t.specs.slice(0, 6).map((spec, i) => (
              <div key={i} className="min-w-0">
                <p className={`text-xs ${specLabelMap[accent]} leading-tight truncate`}>{spec.label}</p>
                <p className="text-white text-xs font-semibold leading-tight mt-0.5 truncate">{spec.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className={`mx-5 border-t ${dividerMap[accent]}`} />

        {/* Actions */}
        <div className="px-5 py-4 flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(detailTarget); }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${btnMap[accent]}`}
          >
            <i className="ri-eye-line" />
            {lang === "zh" ? "查看详情" : "View Details"}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`${detailTarget}#contact`); }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-white/8 border border-white/10 text-gray-300 hover:bg-white/12 text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-customer-service-2-line" />
            {lang === "zh" ? "联系报价" : "Get Quote"}
          </button>
        </div>
      </div>

      {/* Bottom glow line */}
      <div className={`absolute bottom-0 left-0 h-0.5 transition-all duration-400 bg-gradient-to-r ${glowMap[accent]} to-transparent ${hovered ? "w-full" : "w-0"}`} />
    </div>
  );
}
